// Custom form inputs used by the payment config (remote party search, gateway
// select with a "Requesting" empty value, gift card search with balance).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';

import { adminApi, type SelectOption } from '../../api/client';
import type { CrudCustomFieldProps } from '../../crud/types';
import { t, tLabel } from '../../i18n';
import { useBootData } from '../documents/storeSettings';

function useDebounced<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function toOptions(data: unknown): SelectOption[] {
  if (!Array.isArray(data)) return [];
  return data.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    const value = record.value ?? record.id;
    if (value == null) return [];
    return [{ value: value as string, label: String(record.label ?? record.name ?? value) }];
  });
}

// Remote "type to search" select (legacy iView <Select remote>): queries
// `${searchEndpoint}?q=` and resolves the label of a pre-filled id through
// `${detailEndpoint}/{id}`. The resolved label is kept in form[labelField].
export function RemoteSelect({
  props,
  searchEndpoint,
  detailEndpoint,
  labelField
}: {
  props: CrudCustomFieldProps;
  searchEndpoint: string;
  detailEndpoint: string;
  labelField: string;
}) {
  const { field, value, form, error, onChange, setField } = props;
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const query = useDebounced(input);
  const currentLabel = typeof form[labelField] === 'string' ? (form[labelField] as string) : '';
  const resolving = useRef<string | null>(null);

  // Resolve the label of a pre-filled id (e.g. ?customer_id=… deep link).
  useEffect(() => {
    const id = value == null ? '' : String(value);
    if (!id || currentLabel || resolving.current === id) return;
    resolving.current = id;
    adminApi
      .get(`${detailEndpoint}/${id}`)
      .then((response) => {
        const record = response.data as Record<string, unknown>;
        setField(labelField, String(record?.name ?? record?.company ?? id));
      })
      .catch(() => setField(labelField, id));
  }, [value, currentLabel, detailEndpoint, labelField, setField]);

  useEffect(() => {
    if (!query.trim() || query === currentLabel) return;
    let active = true;
    setLoading(true);
    adminApi
      .get(searchEndpoint, { params: { q: query } })
      .then((response) => active && setOptions(toOptions(response.data)))
      .catch(() => active && setOptions([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [query, searchEndpoint, currentLabel]);

  const selected = useMemo<SelectOption | null>(() => {
    if (value == null || value === '') return null;
    return options.find((option) => String(option.value) === String(value)) ?? { value: value as string, label: currentLabel || String(value) };
  }, [options, value, currentLabel]);

  return (
    <Autocomplete
      size="small"
      options={selected && !options.some((option) => String(option.value) === String(selected.value)) ? [selected, ...options] : options}
      value={selected}
      filterOptions={(list) => list}
      loading={loading}
      onInputChange={(_event, next) => setInput(next)}
      onChange={(_event, next) => {
        onChange(next ? next.value : '');
        setField(labelField, next ? next.label : '');
      }}
      isOptionEqualToValue={(option, current) => String(option.value) === String(current.value)}
      getOptionLabel={(option) => option.label ?? String(option.value)}
      noOptionsText={t('orderActions.typeToSearch')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={tLabel(field.label)}
          required={field.required}
          error={Boolean(error)}
          helperText={error ?? field.helperText ?? t('orderActions.typeToSearch')}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );
}

// Plain select whose options may include an empty value with a real label
// (the generic select renders '' as "None").
export function LabeledSelect({ props, options }: { props: CrudCustomFieldProps; options: Array<{ value: string; label: string }> }) {
  const { field, value, error, onChange } = props;
  return (
    <FormControl fullWidth size="small" error={Boolean(error)} required={field.required}>
      <InputLabel shrink>{tLabel(field.label)}</InputLabel>
      <Select displayEmpty notched label={tLabel(field.label)} value={value == null ? '' : String(value)} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <MenuItem key={option.value || '__empty'} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {error || field.helperText ? (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.75 }}>
          {error ?? field.helperText}
        </Typography>
      ) : null}
    </FormControl>
  );
}

type GiftCard = { number?: string; balance?: string | number };

// Gift card number with search (GET gift_cards/search?q=) and balance display.
export function GiftCardInput({ props }: { props: CrudCustomFieldProps }) {
  const { field, value, form, error, onChange } = props;
  const [cards, setCards] = useState<GiftCard[]>([]);
  const [card, setCard] = useState<GiftCard | null>(null);
  const text = value == null ? '' : String(value);
  const query = useDebounced(text);

  useEffect(() => {
    if (!query.trim()) return;
    let active = true;
    adminApi
      .get('gift_cards/search', { params: { q: query } })
      .then((response) => {
        if (!active) return;
        const list = Array.isArray(response.data) ? (response.data as GiftCard[]) : [];
        setCards(list);
        const exact = list.find((entry) => entry.number === query) ?? (list.length === 1 ? list[0] : null);
        setCard(exact);
      })
      .catch(() => active && setCards([]));
    return () => {
      active = false;
    };
  }, [query]);

  const balance = card?.balance != null ? Number(card.balance) : null;
  const amount = Number(form.amount ?? 0);
  const insufficient = balance != null && amount > 0 && balance < amount;

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={cards.map((entry) => String(entry.number ?? ''))}
      inputValue={text}
      onInputChange={(_event, next) => onChange(next)}
      onChange={(_event, next) => {
        onChange(next ?? '');
        setCard(cards.find((entry) => entry.number === next) ?? null);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={tLabel(field.label)}
          required
          error={Boolean(error) || insufficient}
          helperText={
            error ??
            (insufficient
              ? t('orderActions.giftCardBalanceError')
              : balance != null
                ? t('orderActions.giftCardBalance', { balance: balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })
                : undefined)
          }
        />
      )}
    />
  );
}

// Card gateways handled server-side by Services\Payment (omnipay) that need the
// card + billing fields in the admin form (legacy PaymentFormComponent).
export const ONLINE_CARD_GATEWAYS = ['PayPal_Pro', 'PayPal_Rest', 'AuthorizeNetApi_Api'];

// Legacy gateway list: '' (requesting), the configured online gateway (customer
// payments only; Stripe excluded — it needs Stripe.js card elements), cash,
// cheque, gift card, credit card (record only) and other.
export function GatewaySelect({ props }: { props: CrudCustomFieldProps }) {
  const boot = useBootData();
  const online = String(boot.payment.gateway ?? '');
  const options = [
    { value: '', label: t('orderActions.gateway.requesting') },
    ...(props.form.type !== 'supplier' && online && online !== 'Stripe' ? [{ value: online, label: online.replace(/_/g, ' ') }] : []),
    ...['cash', 'cheque', 'gift_card', 'credit_card', 'other'].map((value) => ({ value, label: t(`orderActions.gateway.${value}`) }))
  ];
  const current = props.value == null ? '' : String(props.value);
  if (current && !options.some((option) => option.value === current)) options.push({ value: current, label: current });
  return <LabeledSelect props={props} options={options} />;
}

// <input type="month"> (expiry_date must match Y-m).
export function MonthInput({ props }: { props: CrudCustomFieldProps }) {
  const { field, value, error, onChange } = props;
  return (
    <TextField
      fullWidth
      size="small"
      type="month"
      label={tLabel(field.label)}
      required={field.required}
      value={value == null ? '' : String(value).slice(0, 7)}
      onChange={(event) => onChange(event.target.value)}
      error={Boolean(error)}
      helperText={error ?? field.helperText}
      InputLabelProps={{ shrink: true }}
    />
  );
}
